import React, { useState, useEffect } from 'react';
import { useAppDispatch } from '../../store/hooks';
import {
  Container,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Drawer,
  ListItemIcon,
  ListItemText,
  Divider,
  List,
  Collapse,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Person as PersonIcon,
  ShoppingBag as OrdersIcon,
  Logout as LogoutIcon,
  AdminPanelSettings as AdminIcon,
  Handyman as WorkshopIcon,
  RequestQuote as QuotesIcon,
  Home as HomeIcon,
  Store as StoreIcon,
  Info as InfoIcon,
  ContactMail as ContactIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Brush as BrushIcon,
  Favorite as FavoriteIcon,
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { useLoyalty } from '../../hooks/useLoyalty';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { logout } from '../../store/slices/authSlice';
import { clearCart, removeCoupon } from '../../store/slices/cartSlice';
import { fetchCategoryTree } from '../../store/slices/productsSlice';
import SearchBar from '../SearchBar';
import ScrollToTop from '../ScrollToTop';
import Footer from './Footer';
import CookieConsentBanner from '../CookieConsentBanner';
import MiniCartDropdown from '../MiniCartDropdown';
import { Category } from '../../types';
import { useWishlist } from '../../hooks/useWishlist';
import { useSiteInfo } from '../../hooks/useSiteInfo';
import { telHref } from '../../utils/companyInfo';
import { FEATURES } from '../../config/features';
import GroupHeaderButton from '../groups/GroupHeaderButton';
import GroupDrawerBlock from '../groups/GroupDrawerBlock';
import GroupWelcomeBanner from '../groups/GroupWelcomeBanner';
import { accountMenuLabel, groupPagePath } from '../groups/groupLabels';
import './shell.css';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { totalItems } = useSelector((state: RootState) => state.cart);
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  // Zárt vásárlói csoportok (fejléc-gomb, fiók-menü, mobil blokk).
  const groups = user?.customer_groups ?? [];
  const { data: loyalty } = useLoyalty();
  const { categoryTree } = useSelector((state: RootState) => state.products);

  // Wishlist count (proper ESM import — NE `require()`, az minden rendernél
  // új referenciát ad és a useEffect-et végtelenül refireolná).
  // Reaktív: vendégnél és bejelentkezve is azonnal frissül.
  const wishlistCount = useWishlist().getWishlistCount();
  // Telefonszám a Webshop beállításokból (betöltés közben / üresen nem jelenik meg).
  const sitePhone = (useSiteInfo().data?.company_phone ?? '').trim();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [productsMenuAnchor, setProductsMenuAnchor] = useState<null | HTMLElement>(null);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const [cartAnchorEl, setCartAnchorEl] = useState<null | HTMLElement>(null);

  // Header search-bar (.sb) form state — replaces the old <SearchBar> in the header.
  const [searchInput, setSearchInput] = useState('');
  const [searchCat, setSearchCat] = useState('');

  // Track expanded categories for desktop and mobile separately
  const [expandedDesktopCategories, setExpandedDesktopCategories] = useState<Set<number>>(new Set());
  const [expandedMobileCategories, setExpandedMobileCategories] = useState<Set<number>>(new Set());

  // A kategóriafa nézőfüggő (zárt csoportok): betöltéskor, és belépéskor / kilépéskor újra.
  const viewerId = user?.id;
  useEffect(() => {
    dispatch(fetchCategoryTree());
  }, [dispatch, viewerId]);

  // Scroll listener for header shadow
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCartClick = (event: React.MouseEvent<HTMLElement>) => {
    setCartAnchorEl(event.currentTarget);
  };

  const handleCartClose = () => {
    setCartAnchorEl(null);
  };

  const handleProfileClick = () => {
    setAnchorEl(null);
    navigate('/profil');
  };

  const handleOrdersClick = () => {
    setAnchorEl(null);
    navigate('/rendelesek');
  };

  const handleQuotesClick = () => {
    setAnchorEl(null);
    navigate('/arajanlataim');
  };

  const handleAdminClick = () => {
    setAnchorEl(null);
    navigate('/admin');
  };

  const handleLogout = async () => {
    setAnchorEl(null);
    await dispatch(logout());
    // Prevent the next visitor of this browser from inheriting the previous
    // user's cart / applied coupon.
    dispatch(clearCart());
    dispatch(removeCoupon());
    navigate('/');
  };

  // Header ".sb" search — top-level categories (fallback to a static set) + submit.
  const searchCategories =
    categoryTree && Array.isArray(categoryTree) && categoryTree.length > 0
      ? categoryTree.map((c) => ({ slug: c.slug, name: c.name }))
      : [
          { slug: 'polok', name: 'Pólók' },
          { slug: 'bogrek', name: 'Bögrék' },
          { slug: 'taskak', name: 'Táskák' },
          { slug: 'ajandek', name: 'Ajándék' },
        ];

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const q = searchInput.trim();
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (searchCat) params.set('category', searchCat);
    const qs = params.toString();
    navigate(qs ? `/kereses?${qs}` : '/kereses');
  };

  const navigationItems = [
    { label: 'Főoldal', path: '/', icon: <HomeIcon fontSize="small" /> },
    { label: 'Termékek', path: '/termekek', icon: <StoreIcon fontSize="small" />, hasDropdown: true },
    ...(FEATURES.designEditor ? [{ label: 'Tervező', path: '/design-editor', icon: <BrushIcon fontSize="small" /> }] : []),
    { label: 'Rólunk', path: '/rolunk', icon: <InfoIcon fontSize="small" /> },
    { label: 'Kapcsolat', path: '/kapcsolat', icon: <ContactIcon fontSize="small" /> },
  ];

  const handleProductsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProductsMenuAnchor(event.currentTarget);
  };

  const handleProductsMenuClose = () => {
    setProductsMenuAnchor(null);
  };

  // Toggle expand/collapse for desktop categories
  const toggleDesktopCategory = (categoryId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedDesktopCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  // Recursive function to render category menu items (desktop)
  const renderCategoryMenuItem = (category: Category, level: number = 0): React.ReactNode[] => {
    const hasChildren = category.children && category.children.length > 0;
    const isExpanded = expandedDesktopCategories.has(category.id);

    const items: React.ReactNode[] = [
      <MenuItem
        key={`menu-${category.id}`}
        onClick={() => {
          handleProductsMenuClose();
          navigate(`/termekek?category=${category.slug}`);
        }}
        sx={{
          borderRadius: 1,
          mx: 1,
          my: 0.25,
          py: 0.5,
          pl: 2 + level * 2,
          fontWeight: level === 0 ? 600 : 400,
          fontSize: level === 0 ? '0.95rem' : '0.875rem',
          transition: 'all 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          '&:hover': {
            backgroundColor: 'primary.light',
            color: 'white',
            transform: 'translateX(4px)',
          },
        }}
      >
        {hasChildren && (
          <IconButton
            size="small"
            aria-label={isExpanded ? `${category.name} összecsukása` : `${category.name} kinyitása`}
            onClick={(e) => toggleDesktopCategory(category.id, e)}
            sx={{ mr: 1, p: 0.5 }}
          >
            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        )}
        {!hasChildren && <Box sx={{ width: 32 }} />}
        <ListItemText>{category.name}</ListItemText>
      </MenuItem>
    ];

    if (hasChildren) {
      items.push(
        <Collapse key={`collapse-${category.id}`} in={isExpanded} timeout="auto" unmountOnExit>
          <Box>
            {category.children!.flatMap((child) => renderCategoryMenuItem(child, level + 1))}
          </Box>
        </Collapse>
      );
    }

    return items;
  };

  // Toggle expand/collapse for mobile categories
  const toggleMobileCategory = (categoryId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedMobileCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  // Recursive function to render mobile category items
  const renderMobileCategoryItem = (category: Category, level: number = 0): React.ReactNode[] => {
    const hasChildren = category.children && category.children.length > 0;
    const isExpanded = expandedMobileCategories.has(category.id);

    const items: React.ReactNode[] = [
      <MenuItem
        key={`mobile-menu-${category.id}`}
        onClick={() => {
          setMobileOpen(false);
          navigate(`/termekek?category=${category.slug}`);
        }}
        sx={{
          pl: 4 + level * 2,
          borderRadius: 2,
          my: 0.25,
          py: 0.5,
          fontWeight: level === 0 ? 500 : 400,
          fontSize: level === 0 ? '0.9rem' : '0.85rem',
          transition: 'all 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          '&:hover': {
            backgroundColor: level === 0 ? 'rgba(37, 99, 235, 0.15)' : 'rgba(37, 99, 235, 0.1)',
            transform: 'translateX(4px)',
          },
        }}
      >
        {hasChildren && (
          <IconButton
            size="small"
            aria-label={isExpanded ? `${category.name} összecsukása` : `${category.name} kinyitása`}
            onClick={(e) => toggleMobileCategory(category.id, e)}
            sx={{ mr: 1, p: 0.5 }}
          >
            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        )}
        {!hasChildren && <Box sx={{ width: 32 }} />}
        {category.name}
      </MenuItem>
    ];

    if (hasChildren) {
      items.push(
        <Collapse key={`mobile-collapse-${category.id}`} in={isExpanded} timeout="auto" unmountOnExit>
          <Box>
            {category.children!.flatMap((child) => renderMobileCategoryItem(child, level + 1))}
          </Box>
        </Collapse>
      );
    }

    return items;
  };

  return (
    <Box className="gt5shell" sx={{ flexGrow: 1 }}>
      {/* Ink utility bar (scrolls away above the sticky header) */}
      <Box className="util" sx={{ display: { xs: 'none', sm: 'block' } }}>
        <div className="w utin">
          {/* A korábbi „SOS · 24 óra / Sürgős? Aznap gyártjuk.” állítás tulajdonosi döntésre
              kikerült (2026-10); a sürgős felár a kosárban / rendelésnél továbbra is választható. */}
          <div className="sp">
            {sitePhone && <a href={telHref(sitePhone)}>{sitePhone}</a>}
            <Link to="/gyik">Nyomtatási segédlet</Link>
            <Link to="/ceges-ajanlat">Céges ajánlat</Link>
          </div>
        </div>
      </Box>

      {/* Sticky header (shell.css `.gt5shell header` supplies the sticky/border/shadow) */}
      <Box
        component="header"
        sx={{ boxShadow: scrolled ? '0 6px 24px rgba(20,22,31,.10)' : undefined }}
      >
        <div className="w hd">
          {/* Mobile hamburger */}
          <IconButton
            aria-label="Menü megnyitása"
            onClick={() => setMobileOpen(true)}
            sx={{ display: { md: 'none' }, mr: 0.5, color: '#14161F' }}
          >
            <MenuIcon />
          </IconButton>

          {/* Brand: real logo mark + two-line wordmark */}
          <Link className="br" to="/">
            <img src="/brand/logo-mark.svg" alt="GT Kreatív Sarok" />
            <div>
              <b>Kreatív Sarok</b>
              <span>Egyedi nyomat</span>
            </div>
          </Link>

          {/* Search — prototype `.sb` (select + input + button) */}
          <form className="sb" onSubmit={handleSearchSubmit} role="search">
            <select
              aria-label="Kategória szűrő"
              value={searchCat}
              onChange={(e) => setSearchCat(e.target.value)}
            >
              <option value="">Minden kategória</option>
              {searchCategories.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {c.name}
                </option>
              ))}
            </select>
            <input
              aria-label="Keresés"
              placeholder="Keress mintát, terméket vagy alkalmat…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <button type="submit">🔍 Keresés</button>
          </form>

          {/* Right actions */}
          <div className="hact">
            {groups.length > 0 && (
              <Box component="span" sx={{ display: { xs: 'none', md: 'inline-flex' } }}>
                <GroupHeaderButton groups={groups} />
              </Box>
            )}
            {isAuthenticated ? (
              <button
                className="ha"
                onClick={handleMenu}
                aria-label="Fiók menü"
                aria-haspopup="true"
              >
                Fiók
              </button>
            ) : (
              <>
                <button className="ha" onClick={() => navigate('/bejelentkezes')}>
                  Belépés
                </button>
                <button className="ha" onClick={() => navigate('/regisztracio')}>
                  Regisztráció
                </button>
              </>
            )}

            {/* Wishlist (preserved: count + wishlistUpdated listener) */}
            <button
              className="ha"
              onClick={() => navigate('/kivansaglista')}
              aria-label={`Kívánságlista, ${wishlistCount} termék`}
            >
              <FavoriteIcon fontSize="small" />
              <span className="pts n">{wishlistCount}</span>
            </button>

            {/* GT Pontok — valós egyenleg (csak bejelentkezve), a Fiók pont-paneljére linkel */}
            {isAuthenticated && (
              <Link
                to="/profil?tab=pontok"
                className="ha"
                aria-label={`GT Pontok: ${loyalty?.balance ?? 0}`}
              >
                Pontok <span className="pts n">{loyalty?.balance ?? 0}</span>
              </Link>
            )}

            {/* Cart (red pill) */}
            <button
              className="ha cart"
              onClick={handleCartClick}
              aria-label={`Kosár, ${totalItems} tétel`}
            >
              Kosár <span className="cnt n">{totalItems}</span>
            </button>
          </div>
        </div>

        {/* Account dropdown (opened by the "Fiók" button above) */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            elevation: 3,
            sx: {
              mt: 1.5,
              minWidth: 200,
              borderRadius: 2,
              // A nyíl (::before) miatt a papír overflow-ja visible; a görgetést a
              // lista végzi — különben alacsony képernyőn az alsó pontok (pl.
              // Kijelentkezés) a maxHeight alatt elérhetetlenek.
              overflow: 'visible',
              display: 'flex',
              flexDirection: 'column',
              '& .MuiMenu-list': { overflowY: 'auto', minHeight: 0 },
              '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          }}
        >
          <MenuItem
            onClick={handleProfileClick}
            sx={{
              borderRadius: 1,
              mx: 1,
              my: 0.5,
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: 'rgba(37, 99, 235, 0.1)',
              },
            }}
          >
            <ListItemIcon>
              <PersonIcon fontSize="small" sx={{ color: 'primary.main' }} />
            </ListItemIcon>
            <ListItemText>Profil</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={handleOrdersClick}
            sx={{
              borderRadius: 1,
              mx: 1,
              my: 0.5,
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: 'rgba(16, 185, 129, 0.1)',
              },
            }}
          >
            <ListItemIcon>
              <OrdersIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText>Rendeléseim</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={handleQuotesClick}
            sx={{
              borderRadius: 1,
              mx: 1,
              my: 0.5,
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: 'rgba(245, 158, 11, 0.1)',
              },
            }}
          >
            <ListItemIcon>
              <QuotesIcon fontSize="small" sx={{ color: 'warning.main' }} />
            </ListItemIcon>
            <ListItemText>Árajánlataim</ListItemText>
          </MenuItem>
          {groups.map((group) => (
            <MenuItem
              key={`group-${group.id}`}
              onClick={() => { handleClose(); navigate(groupPagePath(group.slug)); }}
              sx={{ borderRadius: 1, mx: 1, my: 0.5, color: 'success.main', fontWeight: 600 }}
            >
              <ListItemText>{accountMenuLabel(group)}</ListItemText>
            </MenuItem>
          ))}
          {(user?.is_staff || user?.is_superuser) && [
            <Divider key="admin-divider" />,
            <MenuItem
              key="admin-menu-item"
              onClick={handleAdminClick}
              sx={{
                borderRadius: 1,
                mx: 1,
                my: 0.5,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(139, 92, 246, 0.1)',
                },
              }}
            >
              <ListItemIcon>
                <AdminIcon fontSize="small" sx={{ color: 'secondary.main' }} />
              </ListItemIcon>
              <ListItemText>Admin felület</ListItemText>
            </MenuItem>,
            <MenuItem
              key="workshop-menu-item"
              onClick={() => { handleClose(); navigate('/muhely'); }}
              sx={{
                borderRadius: 1,
                mx: 1,
                my: 0.5,
                transition: 'all 0.2s ease',
                '&:hover': { backgroundColor: 'rgba(199, 88, 63, 0.1)' },
              }}
            >
              <ListItemIcon>
                <WorkshopIcon fontSize="small" sx={{ color: 'primary.main' }} />
              </ListItemIcon>
              <ListItemText>Műhely</ListItemText>
            </MenuItem>,
          ]}
          <Divider />
          <MenuItem
            onClick={handleLogout}
            sx={{
              borderRadius: 1,
              mx: 1,
              my: 0.5,
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
              },
            }}
          >
            <ListItemIcon>
              <LogoutIcon fontSize="small" sx={{ color: 'error.main' }} />
            </ListItemIcon>
            <ListItemText>Kijelentkezés</ListItemText>
          </MenuItem>
        </Menu>

        {/* Flat category / nav row (desktop) */}
        <Box className="mn" sx={{ display: { xs: 'none', md: 'block' } }}>
          <div className="w">
            <div className="mni">
              {navigationItems.map((item) =>
                item.hasDropdown ? (
                  <button
                    key={item.path}
                    aria-expanded={Boolean(productsMenuAnchor)}
                    aria-haspopup="true"
                    onClick={handleProductsMenuOpen}
                  >
                    {item.label} ▾
                  </button>
                ) : (
                  <Link key={item.path} to={item.path}>
                    {item.label}
                  </Link>
                )
              )}
              <Link className="hot" to="/termekek">
                Akciók
              </Link>
            </div>
          </div>

          {/* Category mega-menu (recursive tree preserved) */}
          <Menu
            anchorEl={productsMenuAnchor}
            open={Boolean(productsMenuAnchor)}
            onClose={handleProductsMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            PaperProps={{
              elevation: 3,
              sx: {
                mt: 1,
                minWidth: 250,
                borderRadius: 2,
                // Nyíl miatt visible; a lista görget (lásd fiókmenü).
                overflow: 'visible',
                display: 'flex',
                flexDirection: 'column',
                '& .MuiMenu-list': { overflowY: 'auto', minHeight: 0 },
                bgcolor: 'background.paper',
                '&::before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  left: 20,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                },
              },
            }}
          >
            <MenuItem
              component={Link}
              to="/termekek"
              onClick={handleProductsMenuClose}
              sx={{
                fontWeight: 600,
                color: 'primary.main',
                borderRadius: 1,
                mx: 1,
                my: 0.5,
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                },
              }}
            >
              <ListItemIcon>
                <StoreIcon fontSize="small" sx={{ color: 'inherit' }} />
              </ListItemIcon>
              <ListItemText>Összes termék</ListItemText>
            </MenuItem>
            <Divider sx={{ my: 1 }} />
            {categoryTree && Array.isArray(categoryTree) && categoryTree.length > 0 ? (
              categoryTree.flatMap((category) => renderCategoryMenuItem(category))
            ) : (
              <MenuItem disabled>
                <ListItemText>Nincsenek kategóriák</ListItemText>
              </MenuItem>
            )}
          </Menu>
        </Box>
      </Box>

      {isAuthenticated && <GroupWelcomeBanner />}

      {/* Mobile Navigation Drawer */}
      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{
          sx: {
            width: 320,
            backgroundColor: 'background.paper',
            borderRight: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        <Box sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: 2 }}>
            <Box component="img" src="/brand/logo-mark.svg" alt="GT" sx={{ width: 36, height: 36, flexShrink: 0 }} />
            <Box>
              <Box component="span" sx={{ display: 'block', fontWeight: 800, fontSize: '18px', lineHeight: 1, letterSpacing: '-0.02em' }}>
                Kreatív Sarok
              </Box>
              <Box component="span" sx={{ display: 'block', fontWeight: 600, fontSize: '9.5px', letterSpacing: '0.13em', textTransform: 'uppercase', color: '#6B6F7D', mt: '4px' }}>
                Egyedi nyomat
              </Box>
            </Box>
          </Box>
          <GroupDrawerBlock groups={groups} onNavigate={() => setMobileOpen(false)} />
          {/* Mobile/Tablet Search */}
          <Box sx={{ mb: 2 }}>
            <SearchBar size="small" fullWidth placeholder="Keresés termékek között..." />
          </Box>

          <Divider sx={{ mb: 2 }} />

          {navigationItems.map((item) => (
            <React.Fragment key={item.path}>
              {item.hasDropdown ? (
                <>
                  <MenuItem
                    onClick={() => setMobileProductsOpen(!mobileProductsOpen)}
                    sx={{
                      borderRadius: 2,
                      mb: 0.5,
                      fontWeight: 500,
                      color: 'text.primary',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: '#FDE7E3',
                        color: 'primary.main',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ color: 'inherit' }}>{item.icon}</ListItemIcon>
                    <ListItemText>{item.label}</ListItemText>
                    {mobileProductsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </MenuItem>
                  <Collapse in={mobileProductsOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      <MenuItem
                        component={Link}
                        to="/termekek"
                        onClick={() => setMobileOpen(false)}
                        sx={{
                          pl: 4,
                          borderRadius: 2,
                          mb: 0.5,
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          color: 'text.secondary',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: '#FDE7E3',
                            color: 'primary.main',
                            transform: 'translateX(4px)',
                          },
                        }}
                      >
                        Összes termék
                      </MenuItem>
                      {categoryTree && Array.isArray(categoryTree) && categoryTree.length > 0 &&
                        categoryTree.flatMap((category) => renderMobileCategoryItem(category, 1))}
                    </List>
                  </Collapse>
                </>
              ) : (
                <MenuItem
                  component={Link}
                  to={item.path}
                  onClick={() => setMobileOpen(false)}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    fontWeight: 500,
                    color: 'text.primary',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: '#FDE7E3',
                      color: 'primary.main',
                      transform: 'translateX(4px)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: 'inherit' }}>{item.icon}</ListItemIcon>
                  <ListItemText>{item.label}</ListItemText>
                </MenuItem>
              )}
            </React.Fragment>
          ))}
        </Box>
      </Drawer>

      {/* Main Content — xl (1536px), hogy a reskinelt .gt5 .w (1440px) elérje a
          prototípus-szélességet és egy vonalba kerüljön a fejléccel; a nem-reskinelt
          MUI-oldalak (admin, rendelés-részletek, jogi) 1536-nál kapnak, nem lógnak
          teljes szélességben. (#15) */}
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {children}
      </Container>

      {/* Footer */}
      <Footer />

      {/* Scroll to Top Button */}
      <ScrollToTop />

      {/* Cookie Consent Banner */}
      <CookieConsentBanner />

      {/* Mini Cart Dropdown */}
      <MiniCartDropdown
        anchorEl={cartAnchorEl}
        open={Boolean(cartAnchorEl)}
        onClose={handleCartClose}
      />
    </Box>
  );
};

export default Layout;
